import React, { useContext, useEffect, useState } from "react";
import { carService } from '../services/carService';
import { clientService } from '../services/clientService';
import { orderService } from '../services/orderService';
import { RiFileAddLine } from "react-icons/ri";
import ExcelsButtons from "../components/ExcelsButtons";
import GenericTable from "../components/GenericTable";
import ModalForm from "../components/ModalForm";
import OrderDetails from "../components/OrderDetails";
import GenericFilters from '../components/GenericFilters';
import Pagination from "../components/Pagination";
import { getProgressStateText } from "../utils/UITextStates";
import { UserContext } from "../utils/UserContext";
import { filterFormat } from "../utils/filtersFormat";
import { formatTimestamp } from "../utils/formatDate";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../utils/notifications";

const Ordenes = () => {
  const { selectedWorkshop, setActiveOrdersCount } = useContext(UserContext);
  const [workshopID, setWorkshopID] = useState(null);
  const [data, setData] = useState([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startItem, setStartItem] = useState(1);
  const [endItem, setEndItem] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [selectedClientForOrder, setSelectedClientForOrder] = useState("");
  const [selectedProcessState, setSelectedProcessState] = useState(""); // Controlar estado del proceso
  const [vehicleErrorMessage, setVehicleErrorMessage] = useState(""); // Mensaje de error para vehículos
  const [clientErrorMessage, setClientErrorMessage] = useState(""); // Mensaje de error para clientes
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] =
    useState(false);
  const [orderIdToDelete, setOrderIdToDelete] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);


  const updateOrderData = (field, value) => {
    setSelectedOrder((prev) => {
      const updated = { ...(prev ?? {}), [field]: value };
      setData((prevData) =>
        prevData.map((item) =>
          item.noOrden === updated.noOrden ? { ...item, [field]: value } : item,
        ),
      );
      return updated;
    });
  };

  useEffect(() => {
    if (selectedWorkshop) {
      setWorkshopID(selectedWorkshop._id);
    } else {
      notifyWarning("Selecciona un taller");
    }
  }, [selectedWorkshop]);

  useEffect(() => {
    if (workshopID) {
      fetchOrders(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopID]);

  // Obtener clientes para el formulario de nueva orden
  const fetchClients = async () => {
    if (!workshopID) return;
    try {
      const customers = await clientService.getClients();
      if (!customers?.length) {
        setClientErrorMessage(
          "No hay clientes registrados en este taller. Por favor, registre un cliente primero.",
        );
        setAllClients([]);
        return;
      }
      setClientErrorMessage("");
      setAllClients(customers);
    } catch (error) {
      console.error("Error al obtener clientes:", error);
      setClientErrorMessage("Error al cargar clientes del taller.");
      setAllClients([]);
    }
  };

  // Obtener vehículos del cliente seleccionado
  const fetchClientVehicles = async (clientId) => {
    if (!clientId || !workshopID) return;
    try {
      const vehicles = await carService.getClientVehicles(clientId, workshopID);
      if (!vehicles?.length) {
        setVehicleErrorMessage('Este cliente no tiene vehículos registrados. Por favor, registre un vehículo primero.');
        setAllVehicles([]);
        return;
      }
      setVehicleErrorMessage('');
      setAllVehicles(vehicles);
    } catch (error) {
      console.error('Error al obtener vehículos:', error);
      setVehicleErrorMessage('Error al cargar vehículos del cliente.');
      setAllVehicles([]);
    }
  };

  // Crear nuevo cliente
  const handleCreateClient = async (formData) => {
    const { client_name, first_lastname, second_lastname, email, address, phone, RFC, company_name, second_phone } = formData;

    if (!client_name || !first_lastname || !email || !address || !phone) {
      notifyWarning('Todos los campos obligatorios deben ser llenados.');
      return;
    }

    const clientData = {
      name: client_name,
      first_lastname,
      second_lastname: second_lastname || '',
      email,
      address,
      phone,
      RFC: RFC || '',
      company_name: company_name || '',
      second_phone: second_phone || '',
    };

    try {
      await clientService.createClient(clientData);
      setIsCreateClientModalOpen(false);
      notifySuccess('Cliente creado exitosamente');
      await fetchClients();
    } catch (error) {
      console.error('Error al agregar cliente:', error);
      notifyError('Error al crear el cliente');
    }
  };

  // Crear nuevo vehículo
  const handleCreateVehicle = async (formData) => {
    const { marca, sub_marca, matricula, modelo, color, VIN, motor, transmission } = formData;

    if (!marca || !sub_marca || !matricula || !modelo || !transmission) {
      notifyWarning('Los campos Marca, Submarca, Matrícula, Modelo y Transmisión son obligatorios.');
      return;
    }

    if (!selectedClientForOrder) {
      notifyWarning('Primero debes seleccionar un cliente.');
      return;
    }

    const payload = {
      data: {
        marca,
        sub_marca,
        matricula,
        modelo,
        color: color || '',
        VIN: VIN || '',
        motor: motor || '',
        transmission,
        client_id: parseInt(selectedClientForOrder),
      },
    };

    try {
      await carService.createVehicle(payload, workshopID);
      setIsCreateVehicleModalOpen(false);
      notifySuccess('Vehículo creado exitosamente');
      await fetchClientVehicles(selectedClientForOrder);
    } catch (error) {
      console.error('Error al agregar vehículo:', error);
      notifyError('Error al crear el vehículo');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage >= 1 && newPage <= totalPages) {
      if (isSearchMode) {
        applyClientSideSearch(allOrders, filters.search, newPage);
      } else {
        fetchOrders(newPage, filters);
      }
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);

    if (newFilters.dateFrom && newFilters.dateTo) {
      setDateFrom(newFilters.dateFrom);
      setDateTo(newFilters.dateTo);
    }

    setIsSearchMode(false);
    setAllOrders([]);
    setCurrentPage(1);

    // Determinar qué tipo de búsqueda hacer:
    const hasSearch = newFilters.search && newFilters.search.trim() !== '';
    const hasStatus = newFilters.status && newFilters.status.trim() !== '';
    const hasDateRange = newFilters.dateFrom || newFilters.dateTo;

    // Ningún filtro activo → mostrar todas las órdenes
    if (!hasSearch && !hasStatus && !hasDateRange) {
      fetchOrders(1, newFilters);
    }
    // Solo búsqueda de texto → búsqueda en todas las órdenes (client-side)
    else if (hasSearch && !hasStatus && !hasDateRange) {
      handleApplySearchOnly(newFilters.search);
    }
    // Hay status o fechas → usar búsqueda filtrada completa
    else {
      fetchAllOrdersForSearch(newFilters);
    }
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      search: "",
      status: "",
    };
    setFilters(emptyFilters);
    setIsSearchMode(false);
    setDateFrom("");
    setDateTo("");
    setAllOrders([]);
    setCurrentPage(1);
    fetchOrders(1, emptyFilters);
  };

  // Búsqueda client-side solo por texto (sin requerir estado o fechas)
  const handleApplySearchOnly = async (searchTerm) => {
    if (!workshopID) return;

    try {
      setIsLoading(true);

      // Obtener todas las órdenes del taller sin filtros
      const payload = await orderService.getOrders({
        page: 1,
        range: 1000, // Obtener muchas órdenes para búsqueda
        offset: 0,
        workshopId: workshopID,
      });

      const allOrdersData = (payload.data || []).map(mapOrderData);

      // Aplicar búsqueda client-side
      setAllOrders(allOrdersData);
      setIsSearchMode(true);
      applyClientSideSearch(allOrdersData, searchTerm, 1);

      const activeOrders = allOrdersData.filter(order => order.estadoProceso === 'En progreso').length;
      setActiveOrdersCount && setActiveOrdersCount(activeOrders);
    } catch (error) {
      console.error('Error en búsqueda por texto:', error);
      notifyError('Error al buscar órdenes');
    } finally {
      setIsLoading(false);
    }
  };

  const mapOrderData = (order) => ({
    noOrden: order.id,
    processState: order.process_state,
    estadoProceso: getProgressStateText(order.process_state),
    fechaIngreso: formatTimestamp(new Date(order.checkin_date)),
    serviciosSolicitados: order.services_request,
    fechaSalida: order.checkout_date
      ? formatTimestamp(new Date(order.checkout_date))
      : "Pendiente",
    isPayment: order.is_payment ? "Si" : "No",
    total: order.total === null ? "Aun no pagada" : `$${order.total} MXN`,
    requiereFactura: order.requiere_bill ? "Sí" : "No",
    vehiculo: `${order.marca} ${order.sub_marca}`.trim() || "N/A",
    color: order.color || "N/A",
    ubicacion: order.location_on_workshop || "Sin asignar",
    workshopID,
  });

  const searchInOrder = (order, searchTerm) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    const searchableFields = [
      order.noOrden?.toString(),
      order.serviciosSolicitados,
      order.vehiculo,
      order.color,
      order.ubicacion,
      order.estadoProceso,
      order.fechaIngreso,
      order.fechaSalida,
      order.total,
    ];
    return searchableFields.some((field) =>
      field?.toLowerCase().includes(term),
    );
  };

  const applyClientSideSearch = (ordersToSearch, searchTerm, page = 1) => {
    const filtered = searchTerm
      ? ordersToSearch.filter((order) => searchInOrder(order, searchTerm))
      : ordersToSearch;

    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filtered.slice(startIndex, endIndex);

    setData(paginatedData);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage) || 1);
    setCurrentPage(page);
    setStartItem(filtered.length > 0 ? startIndex + 1 : 0);
    setEndItem(Math.min(endIndex, filtered.length));
    setHasNextPage(endIndex < filtered.length);
    setHasPrevPage(page > 1);
  };

  const fetchAllOrdersForSearch = async (appliedFilters) => {
    if (!workshopID) return;
    setIsLoading(true);
    try {
      const effectiveDateFrom = appliedFilters?.dateFrom || "";
      const effectiveDateTo = appliedFilters?.dateTo || "";

      const filtersQuery = filterFormat({
        order_checkout_date: { from: effectiveDateFrom, to: effectiveDateTo },
        order_process_state: appliedFilters.status
      });

      if (!filtersQuery) {
        notifyWarning('Por favor elige un filtro para continuar');
        return;
      }

      const payload = await orderService.getFilteredOrders({ filtersQuery, workshopId: workshopID });
      const orders = (payload.data || []).map(mapOrderData);

      setAllOrders(orders);
      setIsSearchMode(true);
      applyClientSideSearch(orders, appliedFilters.search, 1);

      const activeOrders = orders.filter(order => order.estadoProceso === 'En progreso').length;
      setActiveOrdersCount && setActiveOrdersCount(activeOrders);
    } catch (error) {
      console.error('Error cargando órdenes para búsqueda: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async (page = 1, appliedFilters = filters) => {
    if (!workshopID) return;
    setIsLoading(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const payload = await orderService.getOrders({
        page,
        range: itemsPerPage,
        offset,
        status: appliedFilters.status,
        workshopId: workshopID,
      });

      const orders = (payload.data || []).map(mapOrderData);
      const pagination = payload.pagination || {};

      setData(orders);
      setTotalItems(pagination.total || 0);
      setTotalPages(pagination.pages || 1);
      setItemsPerPage(pagination.itemsPerPage || itemsPerPage);
      setCurrentPage(page);

      setStartItem(pagination.init || 0);
      setEndItem(pagination.end || 0);
      setHasNextPage(!!pagination.hasNextPage);
      setHasPrevPage(!!pagination.hasPrevPage);

      const activeOrders = orders.filter(order => order.estadoProceso === 'En progreso').length;
      setActiveOrdersCount && setActiveOrdersCount(activeOrders);
    } catch (error) {
      console.error('Error trayendo orders: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (rowData) => {
    setIsDetailModalOpen(true);
    setSelectedOrder(rowData);
  };

  const actionHandlers = {
    edit: handleViewDetails,
    delete: (order) => {
      setOrderIdToDelete(order.noOrden);
      setIsDeleteConfirmModalOpen(true);
    },
  };

  const handleAddOrder = async (formData) => {
    const { client_id, car_id, services_request, requiere_bill, process_state, scheduled_date } = formData;

    if (!client_id || !car_id || !services_request || !process_state || !requiere_bill) {
      notifyWarning('⚠️ Todos los campos son obligatorios. Por favor, completa el formulario.');
      return;
    }

    if (process_state === 'SCHEDULE' && !scheduled_date) {
      notifyWarning('⚠️ Para órdenes programadas, la fecha programada es obligatoria.');
      return;
    }

    const checkinDate = process_state === 'SCHEDULE' && scheduled_date
      ? new Date(scheduled_date).toISOString()
      : new Date().toISOString();

    const payload = {
      data: {
        client_id: parseInt(client_id),
        car_id: parseInt(car_id),
        services_request,
        requiere_bill: requiere_bill === 'true' || requiere_bill === true,
        process_state,
        checkin_date: checkinDate,
      },
    };

    try {
      await orderService.createOrder(payload, workshopID);
      setIsAddOrderModalOpen(false);
      setSelectedClientForOrder('');
      setAllVehicles([]);
      notifySuccess('¡Orden creada exitosamente!');
      fetchOrders(1);
    } catch (error) {
      console.error('Error al crear orden:', error);
      notifyError(`Error al crear la orden: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderIdToDelete) return;
    try {
      await orderService.deleteOrder(
        orderIdToDelete,
        { is_deleted: 1 },
        workshopID,
      );

      notifySuccess('Orden eliminada exitosamente');
      fetchOrders(1);
    } catch (error) {
      console.error('Error al eliminar orden:', error);
      notifyError('Error al eliminar la orden');
    } finally {
      setOrderIdToDelete(null);
      setIsDeleteConfirmModalOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setOrderIdToDelete(null);
    setIsDeleteConfirmModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedOrder(null);
  };


  const orderFormFields = [
    {
      name: "client_id",
      label: "Cliente",
      type: "select",
      required: true,
      options: allClients.map((client) => ({
        value: client.id,
        label: client.company_name || `${client.name} ${client.first_lastname}`,
      })),
      onChange: (value) => {
        setSelectedClientForOrder(value);
        fetchClientVehicles(value);
      },
      onAddNew: () => setIsCreateClientModalOpen(true),
      addButtonTitle: "Agregar nuevo cliente",
    },
    ...(clientErrorMessage
      ? [
        {
          name: "client_error",
          label: "",
          type: "error-message",
          message: clientErrorMessage,
        },
      ]
      : []),
    {
      name: "car_id",
      label: "Vehículo",
      type: "select",
      required: true,
      options: allVehicles.map((vehicle) => ({
        value: vehicle.id,
        label: `${vehicle.marca} ${vehicle.sub_marca} - ${vehicle.matricula}`,
      })),
      disabled: !selectedClientForOrder,
      onAddNew: selectedClientForOrder
        ? () => setIsCreateVehicleModalOpen(true)
        : undefined,
      addButtonTitle: "Agregar nuevo vehículo",
    },
    ...(vehicleErrorMessage
      ? [
        {
          name: "vehicle_error",
          label: "",
          type: "error-message",
          message: vehicleErrorMessage,
        },
      ]
      : []),
    {
      name: "services_request",
      label: "Servicios Solicitados",
      type: "textarea",
      required: true,
      rows: 4,
    },
    {
      name: "process_state",
      label: "Estado Inicial",
      type: "select",
      required: true,
      options: [
        { value: "SCHEDULE", label: "Programado" },
        { value: "IN_PROGRESS", label: "En Proceso" },
      ],
      onChange: (value) => {
        setSelectedProcessState(value);
      },
    },
    ...(selectedProcessState === "SCHEDULE"
      ? [
        {
          name: "scheduled_date",
          label: "Fecha Programada",
          type: "date",
          required: true,
          defaultValue: new Date().toISOString().split("T")[0],
        },
      ]
      : []),
    {
      name: "requiere_bill",
      label: "¿Requiere Factura?",
      type: "select",
      required: true,
      options: [
        { value: "false", label: "No" },
        { value: "true", label: "Sí" },
      ],
    },
  ];

  const columns = React.useMemo(
    () => [
      { Header: "No. Orden", accessor: "noOrden" },
      { Header: "Estado del Proceso", accessor: "estadoProceso" },
      { Header: "Fecha de Ingreso", accessor: "fechaIngreso" },
      { Header: "Servicios Solicitados", accessor: "serviciosSolicitados" },
      { Header: "Fecha de Salida", accessor: "fechaSalida" },
      { Header: "Pagada", accessor: "isPayment" },
      { Header: "Total", accessor: "total" },
      { Header: "Requiere Factura", accessor: "requiereFactura" },
    ],
    [],
  );

  return (
    <div className="category-container">
      <div className="category-header">
        <h1 className="category-title">Órdenes</h1>
        <button
          className="add-btn add-btn-icon"
          onClick={() => {
            fetchClients();
            setVehicleErrorMessage("");
            setClientErrorMessage("");
            setIsAddOrderModalOpen(true);
          }}
        >
          <RiFileAddLine aria-hidden="true" />
          <span>Nueva orden</span>
        </button>
      </div>
      <GenericFilters
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchPlaceholder="Número de orden, servicios..."
        filterLabel="Filtros"
        showStatus={true}
        statusOptions={[
          { value: '', label: 'Todos los estados' },
          { value: 'SCHEDULE', label: 'Programado' },
          { value: 'IN_PROGRESS', label: 'En progreso' },
          { value: 'REVIEW', label: 'En revisión' },
          { value: 'COMPLETE_NOT_PAY', label: 'Completado - Pendiente de pago' },
          { value: 'COMPLETE_AND_PAY', label: 'Completado - Pagado' },
        ]}
        showDateRange={true}
      />
      <ExcelsButtons columns={columns} data={data} />
      <GenericTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        actionList={['edit', 'delete']}
        actionHandlers={actionHandlers}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        startItem={startItem}
        endItem={endItem}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        isLoading={isLoading}
      />

      {isDetailModalOpen && selectedOrder && (
        <ModalForm
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title={`Orden de Trabajo: ${selectedOrder.noOrden}`}
          size="large"
          footer={
            <button
              className="cancel-btn"
              onClick={() => setIsDetailModalOpen(false)}
            >
              Cerrar
            </button>
          }
        >
          <OrderDetails
            isOpen={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
            orderData={selectedOrder}
            updateOrderData={updateOrderData}
          />
        </ModalForm>
      )}

      {/* Modal para añadir orden */}
      {isAddOrderModalOpen && (
        <ModalForm
          isOpen={isAddOrderModalOpen}
          onClose={() => {
            setIsAddOrderModalOpen(false);
            setSelectedClientForOrder("");
            setAllVehicles([]);
            setSelectedProcessState("");
            setVehicleErrorMessage("");
            setClientErrorMessage("");
          }}
          onSubmit={handleAddOrder}
          title="Crear Nueva Orden"
          size="medium"
          fields={orderFormFields}
        />
      )}

      {/* Modal anidado para crear cliente */}
      {isCreateClientModalOpen && (
        <ModalForm
          isOpen={isCreateClientModalOpen}
          onClose={() => setIsCreateClientModalOpen(false)}
          onSubmit={handleCreateClient}
          title="Agregar Nuevo Cliente"
          size="medium"
          className="stacked"
          fields={[
            {
              name: "client_name",
              label: "Nombre",
              type: "text",
              required: true,
            },
            {
              name: "first_lastname",
              label: "Apellido Paterno",
              type: "text",
              required: true,
            },
            {
              name: "second_lastname",
              label: "Apellido Materno",
              type: "text",
              required: false,
            },
            {
              name: "company_name",
              label: "Nombre de Empresa",
              type: "text",
              required: false,
            },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "phone", label: "Teléfono", type: "tel", required: true },
            {
              name: "second_phone",
              label: "Teléfono Secundario",
              type: "tel",
              required: false,
            },
            {
              name: "address",
              label: "Dirección",
              type: "text",
              required: true,
            },
            { name: "RFC", label: "RFC", type: "text", required: false },
          ]}
        />
      )}

      {/* Modal anidado para crear vehículo */}
      {isCreateVehicleModalOpen && (
        <ModalForm
          isOpen={isCreateVehicleModalOpen}
          onClose={() => setIsCreateVehicleModalOpen(false)}
          onSubmit={handleCreateVehicle}
          title="Agregar Nuevo Vehículo"
          size="medium"
          className="stacked"
          fields={[
            {
              name: "marca",
              label: "Marca *",
              type: "text",
              required: true,
              placeholder: "Ej: Toyota, Ford, Nissan...",
            },
            {
              name: "sub_marca",
              label: "Submarca *",
              type: "text",
              required: true,
              placeholder: "Ej: Corolla, Focus, Versa...",
            },
            {
              name: "matricula",
              label: "Matrícula / Placas *",
              type: "text",
              required: true,
              placeholder: "Número de placas...",
            },
            {
              name: "modelo",
              label: "Año / Modelo *",
              type: "text",
              required: true,
              placeholder: "Ej: 2020, 2021...",
            },
            {
              name: "color",
              label: "Color",
              type: "text",
              required: false,
              placeholder: "Color del vehículo...",
            },
            {
              name: "transmission",
              label: "Tipo de transmisión *",
              type: "select",
              required: true,
              options: [
                { value: "MANUAL", label: "Manual" },
                { value: "AUTO", label: "Automática" },
                { value: "CVT", label: "CVT" },
              ],
            },
            {
              name: "VIN",
              label: "Número de serie (VIN)",
              type: "text",
              required: false,
              placeholder: "17 caracteres alfanuméricos...",
            },
            {
              name: "motor",
              label: "Número de motor",
              type: "text",
              required: false,
              placeholder: "Identificación del motor...",
            },
          ]}
        />
      )}

      {/* Modal de confirmación para eliminar orden */}
      {isDeleteConfirmModalOpen && (
        <ModalForm
          isOpen={isDeleteConfirmModalOpen}
          onClose={handleCancelDelete}
          title="Confirmar Eliminación"
          size="small"
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="cancel-btn" onClick={handleCancelDelete}>
                Cancelar
              </button>
              <button
                className="delete-btn"
                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                onClick={handleDeleteOrder}
              >
                Eliminar
              </button>
            </div>
          }
        >
          <p>¿Estás realmente seguro de que deseas eliminar esta orden? Esta acción no se puede deshacer.</p>
        </ModalForm>
      )}
    </div>
  );
};

export default Ordenes;
